/**
 * Ruby Theme - Passionate Red Tones
 * WCAG AA Compliant
 */

import { ColorPalette } from '../types';
import { ThemeMetadata } from './types';
import { IconName } from '@/icons/types';

/**
 * Ruby theme color palette
 * All colors are WCAG AA compliant for accessibility
 */
export const rubyTheme: ColorPalette = {
  // Primary Colors
  primary: '#C41E3A', // Ruby Red - Contrast: 5.8:1 (WCAG AA) with white text
  success: '#2B7309', // Deep Green - Contrast: 4.83:1 (WCAG AA) with white text
  purple: '#9333EA', // Purple - Contrast: 4.8:1 (WCAG AA) with white text
  blue: '#1D4ED8', // Deeper Blue - Contrast: 7.8:1 (WCAG AA) with white text
  error: '#B91C1C', // Deep Red - Contrast: 6.4:1 (WCAG AA) with white text
  rust: '#DC2626', // Bright Rust - Contrast: 5.3:1 (WCAG AA) with white text
  teal: '#0F766E', // Deep Teal - Contrast: 5.3:1 (WCAG AA) with white text
  lightPurple: '#C084FC', // Light Purple
  forest: '#16A34A', // Forest Green
  coral: '#FCA5A5', // Coral Pink

  // Neutrals
  dark: '#450A0F', // Deep Ruby Dark
  medium: '#7F1D1D', // Ruby Medium
  light: '#991B1B', // Ruby Light
  background: '#FEE2E2', // Soft Rose (darker for cards)
  offWhite: '#FFFAFA', // Almost White with hint of rose (lighter for columns)

  // UI States
  hover: {
    background: '#D93251', // Brighter ruby red - Contrast: 4.5:1 (WCAG AA) with white text
    text: '#FFFFFF',
  },
  active: {
    background: '#C41E3A', // Ruby Red - Contrast: 5.8:1 (WCAG AA) with white text
    text: '#FFFFFF',
  },
  disabled: {
    background: '#FEE2E2',
    text: '#7F1D1D', // Contrast: 6.1:1 (WCAG AA) on disabled background
  },

  // Semantic Colors
  text: {
    primary: '#450A0F', // Deep Ruby - Contrast: 14.2:1 (WCAG AAA) on background
    secondary: '#7F1D1D', // Ruby Medium - Contrast: 8.3:1 (WCAG AAA) on background
    tertiary: '#991B1B', // Ruby Light - Contrast: 6.8:1 (WCAG AA) on background
    inverse: '#FFFFFF',
  },

  border: {
    default: '#7F1D1D', // Contrast: 8.3:1 (WCAG AA for UI) on background
    light: '#991B1B', // Contrast: 6.8:1 (WCAG AA for UI) on background
    dark: '#450A0F', // Contrast: 14.2:1 (WCAG AAA) on background
  },

  icon: {
    primary: '#450A0F', // Contrast: 14.2:1 (WCAG AAA) on background
    secondary: '#7F1D1D', // Contrast: 8.3:1 (WCAG AAA) on background
  },

  // Warning states
  warning: {
    background: '#FEF3C7', // Light amber background
    border: '#C41E3A', // Ruby border
    text: '#92400E', // Dark amber - Contrast: 7.8:1 on warning background
  },

  // Highlighting
  highlight: {
    background: '#FEE2E2', // Soft rose highlight
    pulse: '#FCA5A5', // Rose pulse
  },

  // Focus states
  focus: {
    ring: '#C41E3A', // Ruby Red
    background: '#FEE2E2', // Soft rose
  },

  // Reflection badge system
  reflection: {
    understood: { bg: '#F0FDF4', text: '#15803D' },
    stillThinking: { bg: '#ECFEFF', text: '#0E7490' },
    needHelp: { bg: '#FEF2F2', text: '#B91C1C' },
  },
};

/**
 * Ruby theme metadata for UI display
 */
export const rubyMetadata: ThemeMetadata = {
  label: 'Ruby',
  icon: IconName.HEART,
  preview: 'linear-gradient(135deg, #C41E3A 0%, #DC2626 100%)',
};
